'use client';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { CartDto } from '@cineamo/api/models/cart/CartDto.types';
import { TicketDto } from '@cineamo/api/models/ticket/TicketDto';

import {
    appleWalletGroupTicketUrl,
    appleWalletTicketUrl,
    getGoogleWalletTicket,
    getGoogleWalletGroupTicket,
    downloadPdfTicket,
    downloadPdfTickets,
    getCartInvoice,
    getTickets,
    downloadPdfGroupTicket,
    getCartByUuid
} from '@cineamo/api/resources/cart/cart-api';
import { getCartConcessions } from '@cineamo/api/resources/cart-concession/cart-concession-api';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { getAllListItems } from '@cineamo/ui/src/helpers/get-all-list-items';
import { getLanguageCodeFromString } from '@cineamo/ui/src/helpers/locale-helper';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { ButtonStyle } from '@cineamo/ui/src/button/ButtonStyle.enum';
import Button from '@cineamo/ui/src/button/NewButton';
import { IconDownload16 } from '@cineamo/ui/src/icons/ic-download';
import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconTick16 } from '@cineamo/ui/src/icons/ic-tick';
import ProgressBarAnimation from '@cineamo/ui/src/loading/ProgressBarAnimation';

import { getRefusedConcessionsRefund } from '@cineamo/composables/src/order/refused-concessions-helper';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import { BOOKING_QUERY_ROOTS } from './booking-query-keys';
import { reportBookingError, setBookingContext, trackBookingEvent } from './booking-telemetry-client';
import { paidCartVerdict } from './booking-verdict';
import PaymentFailedView from './PaymentFailedView';

/** Upper bound for polling an order that stays without a verdict: 60 reads, two seconds apart. */
const ORDER_SETTLED_POLL_LIMIT = 60;
const addToAppleWalletDE = loadAsset('/images/badges/im-addToAppleWallet-DE.svg');
const addToAppleWalletEN = loadAsset('/images/badges/im-addToAppleWallet-EN.svg');
const addToGoogleWalletDE = loadAsset('/images/badges/im-addToGoogleWallet-DE.svg');
const addToGoogleWalletEN = loadAsset('/images/badges/im-addToGoogleWallet-EN.svg');

export type TicketWithPass = {
    ticket: TicketDto;
    googleWalletJwt: string | null;
};

type TicketRowProps = {
    title: string;
    cartUuid: CartDto['uuid'];
    cartTicketCount: number;
    ticketWithPass: TicketWithPass;
};

const TicketRow: React.FC<TicketRowProps> = ({ ticketWithPass, title, cartUuid, cartTicketCount }) => {
    const locale = useLocale();
    const isGerman = getLanguageCodeFromString(locale) === 'de';
    const { track } = useAnalytics();
    const t = useTranslations('tickets');

    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);

    const AddToAppleWallet = (
        <Image
            width={153}
            height={48}
            className="w-[153px] h-[48px]"
            src={isGerman ? addToAppleWalletDE : addToAppleWalletEN}
            alt={t('add-to-apple-wallet')}
        />
    );
    const AddToGoogleWallet = (
        <Image
            width={153}
            height={48}
            className="w-[153px] h-[48px]"
            src={isGerman ? addToGoogleWalletDE : addToGoogleWalletEN}
            alt={t('add-to-google-wallet')}
        />
    );

    const onAppleWalletClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'Apple Wallet',
            type: 'single',
            cartTicketCount: cartTicketCount
        });
    }, [track, cartTicketCount]);

    const onGoogleWalletClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'Google Wallet',
            type: 'single',
            cartTicketCount: cartTicketCount
        });
        window.open('https://pay.google.com/gp/v/save/' + ticketWithPass.googleWalletJwt, '_blank');
    }, [ticketWithPass, track, cartTicketCount]);

    const onPdfClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'PDF',
            type: 'single',
            cartTicketCount: cartTicketCount
        });
        setIsPdfLoading(true);
        downloadPdfTicket(cartUuid, ticketWithPass.ticket.id).then((result) => {
            if (result === null) {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('error.ticket-download-failed')
                });
            }
            setIsPdfLoading(false);
        });
    }, [cartUuid, ticketWithPass.ticket.id, track, cartTicketCount, t]);

    return (
        <div
            className={cn(
                'rounded-6 flex flex-col lg:flex-row space-y-16 lg:space-y-0 lg:space-x-16 p-16',
                'bg-[linear-gradient(180deg,rgba(var(--on-background-color-rgb),0.05)-0.14%,rgba(var(--on-background-color-rgb),0.10)99.86%)]'
            )}>
            <div className="grow">
                <div className="text-body-bold-16">{title}</div>
                <div className="text-body-14">
                    {t('ticket-number', { ticketNumber: ticketWithPass.ticket.ticketSystemId ?? '' })}
                </div>
            </div>
            <div className="flex flex-row space-x-16 max-w-[100%] overflow-scroll">
                <Button
                    style={ButtonStyle.surface}
                    leadingIcon={isPdfLoading ? <IconLoading16 className="animate-spin" /> : <IconDownload16 />}
                    onClick={onPdfClick}>
                    PDF
                </Button>
                <a
                    className="shrink-0"
                    href={appleWalletTicketUrl(cartUuid, ticketWithPass.ticket.id)}
                    onClick={onAppleWalletClick}>
                    {AddToAppleWallet}
                </a>
                {ticketWithPass?.googleWalletJwt && (
                    <button
                        className="shrink-0"
                        onClick={onGoogleWalletClick}>
                        {AddToGoogleWallet}
                    </button>
                )}
            </div>
        </div>
    );
};

type GroupTicketRowProps = {
    cartUuid: CartDto['uuid'];
    cartTicketCount: number;
};
const GroupTicketRow: React.FC<GroupTicketRowProps> = ({ cartUuid, cartTicketCount }) => {
    const locale = useLocale();
    // See TicketRow above: the language subtag, not the whole locale.
    const isGerman = getLanguageCodeFromString(locale) === 'de';
    const { track } = useAnalytics();
    const t = useTranslations('tickets');

    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);

    const { data: ticketId } = useQuery({
        queryKey: [BOOKING_QUERY_ROOTS.groupTicketId, cartUuid],
        queryFn: async () => {
            const response = await getCartInvoice(cartUuid, 'json');
            return response?.bookingNumber;
        }
    });

    const { data: googleWalletJwt } = useQuery({
        queryKey: [BOOKING_QUERY_ROOTS.groupTicketGoogleWallet, cartUuid],
        queryFn: async () => {
            return await getGoogleWalletGroupTicket(cartUuid);
        }
    });

    const AddToAppleWallet = (
        <Image
            width={153}
            height={48}
            className="w-[153px] h-[48px]"
            src={isGerman ? addToAppleWalletDE : addToAppleWalletEN}
            alt={t('add-to-apple-wallet')}
        />
    );
    const AddToGoogleWallet = (
        <Image
            width={153}
            height={48}
            className="w-[153px] h-[48px]"
            src={isGerman ? addToGoogleWalletDE : addToGoogleWalletEN}
            alt={t('add-to-google-wallet')}
        />
    );

    const onAppleWalletClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'Apple Wallet',
            type: 'group',
            cartTicketCount: cartTicketCount
        });
    }, [track, cartTicketCount]);

    const onGoogleWalletClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'Google Wallet',
            type: 'group',
            cartTicketCount: cartTicketCount
        });
        window.open('https://pay.google.com/gp/v/save/' + googleWalletJwt, '_blank');
    }, [googleWalletJwt, track, cartTicketCount]);

    const onPdfClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'PDF',
            type: 'group',
            cartTicketCount: cartTicketCount
        });
        setIsPdfLoading(true);
        downloadPdfGroupTicket(cartUuid).then((result) => {
            if (result === null) {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('error.ticket-download-failed')
                });
            }
            setIsPdfLoading(false);
        });
    }, [cartUuid, track, cartTicketCount, t]);

    return (
        <div
            className={cn(
                'rounded-6 flex flex-col lg:flex-row space-y-16 lg:space-y-0 lg:space-x-16 p-16',
                'bg-[linear-gradient(180deg,rgba(var(--primary-color-rgb),0.05)-0.14%,rgba(var(--primary-color-rgb),0.10)99.86%)]'
            )}>
            <div className="grow">
                <div className="text-body-bold-16">{t('group-ticket')}</div>
                <div className="text-body-14">{t('ticket-number', { ticketNumber: ticketId ?? '' })}</div>
            </div>
            <div className="flex flex-row space-x-16 max-w-[100%] overflow-scroll">
                <Button
                    style={ButtonStyle.surface}
                    leadingIcon={isPdfLoading ? <IconLoading16 className="animate-spin" /> : <IconDownload16 />}
                    onClick={onPdfClick}>
                    PDF
                </Button>
                <a
                    className="shrink-0"
                    href={appleWalletGroupTicketUrl(cartUuid)}
                    onClick={onAppleWalletClick}>
                    {AddToAppleWallet}
                </a>
                {googleWalletJwt && (
                    <button
                        className="shrink-0"
                        onClick={onGoogleWalletClick}>
                        {AddToGoogleWallet}
                    </button>
                )}
            </div>
        </div>
    );
};

const PaymentSucceededView: React.FC = () => {
    const { paidCartUuid, concessionPickupLocation, showing } = useCartProvider();
    // The cinema sells; Cineamo sells in its name (the website imprint), so the thanks go to the cinema.
    const cinemaName = showing?._embedded?.cinema?.name;
    const t = useTranslations('tickets');
    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
    const { track } = useAnalytics();
    const formatter = useFormat();

    // Every read counts toward the limit, failed ones too: a read that fails keeps the last answer (CIN-15436).
    const [pollCount, setPollCount] = useState(0);
    const isPollLimitReached = pollCount >= ORDER_SETTLED_POLL_LIMIT;
    // One report per visit: a bad connection would fail every one of the 60 reads.
    const hasReportedPollFailure = useRef(false);
    const { data: paidCart, refetch: refetchPaidCart } = useQuery({
        queryKey: [BOOKING_QUERY_ROOTS.paidCart, paidCartUuid],
        queryFn: async () => {
            setPollCount((count) => count + 1);
            try {
                return await getCartByUuid(paidCartUuid);
            } catch (error) {
                if (!hasReportedPollFailure.current) {
                    hasReportedPollFailure.current = true;
                    reportBookingError({
                        step: 'order-poll',
                        code: 'read-failed',
                        error,
                        level: 'warning',
                        context: { cart_uuid: paidCartUuid }
                    });
                }
                throw error;
            }
        },
        // Fulfillment runs after the payment; wait for its verdict before promising tickets.
        refetchInterval: (query) =>
            paidCartVerdict(query.state.data?.orderState, isPollLimitReached) === 'pending' ? 2000 : false
    });
    const refusedConcessionsRefund = getRefusedConcessionsRefund(paidCart);
    const verdict = paidCartVerdict(paidCart?.orderState, isPollLimitReached);
    const isConfirmed = verdict === 'confirmed';

    // The funnel's end: what the buyer was told, once per verdict. 'pending' is not an end.
    const orderId = paidCart?.orderId;
    useEffect(() => {
        if (orderId) setBookingContext({ order_id: orderId });
    }, [orderId]);
    const reportedVerdict = useRef<string | null>(null);
    useEffect(() => {
        if (verdict === 'pending' || reportedVerdict.current === verdict) return;
        reportedVerdict.current = verdict;
        const properties = { verdict, order_state: paidCart?.orderState, polls: pollCount };
        if (verdict === 'confirmed') {
            trackBookingEvent('booking_order_fulfilled', properties);
        } else if (verdict === 'unconfirmed') {
            // The poll gave up: the payment is held, the order never settled. Someone must look (CIN-15436).
            trackBookingEvent('booking_poll_limit_reached', properties);
            reportBookingError({ step: 'order-poll', code: 'limit-reached', level: 'warning', extra: properties });
        } else {
            trackBookingEvent('booking_order_failed', properties);
        }
    }, [verdict, paidCart?.orderState, pollCount]);

    const { data: snacks = [] } = useQuery({
        queryKey: [BOOKING_QUERY_ROOTS.paidCartConcessions, paidCartUuid],
        queryFn: async () => (await getCartConcessions(paidCartUuid))?._embedded?.concessions ?? [],
        enabled: isConfirmed
    });

    const { data: ticketsWithPasses = [], isLoading: isLoadingTickets } = useQuery({
        queryKey: [BOOKING_QUERY_ROOTS.ticketsWithPasses, paidCartUuid],
        // The ticket system assigns ticketSystemId a few seconds after checkout, and every download format
        // needs it. Observed round trips reach ~10s, so allow 20 (CIN-15227).
        retry(failureCount) {
            return failureCount < 20;
        },
        retryDelay: 1000,
        queryFn: async () =>
            await getAllListItems({
                queryFn: async (page: number) => {
                    const getTicketsResponse = await getTickets(paidCartUuid, {
                        page
                    });
                    const tickets = getTicketsResponse?._embedded?.tickets || [];
                    // The list answers 200 with a null ticketSystemId while the booking is still being
                    // issued, so nothing here fails on its own: throw to drive the retry above, before the
                    // wallet prefetch below asks for passes that cannot be built yet.
                    if (tickets.some((ticket) => !ticket.ticketSystemId)) {
                        throw new Error('Tickets are not issued by the ticket system yet');
                    }
                    const ticketsWithPasses = await Promise.all(
                        tickets.map(async (ticket) => {
                            let googleWalletJwt = null;
                            try {
                                googleWalletJwt = await getGoogleWalletTicket(paidCartUuid, ticket.id);
                            } catch (error) {
                                console.error(
                                    'PaymentSucceededView.tsx Error: Failed to get Google Wallet ticket',
                                    error
                                );
                            }
                            return {
                                ticket,
                                googleWalletJwt
                            };
                        }) || []
                    );
                    return {
                        ...getTicketsResponse,
                        _embedded: {
                            ticketsWithPasses: ticketsWithPasses
                        }
                    };
                },
                listAccessor: 'ticketsWithPasses'
            }),
        refetchInterval: false,
        enabled: isConfirmed
    });

    if (verdict === 'booking-failed') {
        return (
            <PaymentFailedView
                title={t('booking-failed')}
                message={t('booking-failed-message')}
                orderId={paidCart?.orderId}
            />
        );
    }
    if (verdict === 'payment-failed') {
        return <PaymentFailedView orderId={paidCart?.orderId} />;
    }
    if (verdict === 'pending' || verdict === 'unconfirmed') {
        const isUnconfirmed = verdict === 'unconfirmed';
        return (
            <div
                role="status"
                aria-live="polite"
                className="flex flex-col items-center space-y-16 container max-w-[600px] mx-auto pt-[48px] px-16">
                {!isUnconfirmed && <ProgressBarAnimation />}
                <h2 className="text-headline-24 text-center">
                    {t(isUnconfirmed ? 'booking-status.unconfirmed-title' : 'booking-status.confirming-title')}
                </h2>
                <p className="text-body-14 whitespace-pre-wrap text-center">
                    {t(isUnconfirmed ? 'booking-status.unconfirmed-message' : 'booking-status.confirming-message')}
                </p>
                {paidCart?.orderId ? (
                    <p className="text-body-14 opacity-70">{t('order-number', { orderNumber: paidCart.orderId })}</p>
                ) : null}
                {isUnconfirmed && (
                    <>
                        <p className="text-body-14 text-center">{t('booking-status.support')}</p>
                        <Button
                            onClick={() => {
                                setPollCount(0);
                                void refetchPaidCart();
                            }}>
                            {t('booking-status.check-again')}
                        </Button>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col space-y-32 max-w-[900px] mx-auto px-16">
            <div className="flex flex-col items-center space-y-32 pb-48">
                <div className="flex flex-col items-center space-y-16">
                    <div className="w-48 h-48 flex items-center justify-center rounded-full bg-green">
                        <IconTick16 className="w-8/12 h-auto text-darkBlue" />
                    </div>
                    <h2 className="text-headline-24 text-center">{t('payment-successful')}</h2>
                    <p className="text-body-14 whitespace-pre-wrap text-center">
                        {cinemaName
                            ? t('payment-successful-thanks-cinema', { cinema: cinemaName })
                            : t('payment-successful-thanks')}
                        {'\n'}
                        {t('payment-successful-email')}
                    </p>
                    {refusedConcessionsRefund !== null && (
                        <p
                            role="status"
                            className="text-body-14 text-center rounded-12 border border-white/20 p-16">
                            {t('snacks-refused-refund', {
                                amount: formatter.number.formatCurrency(refusedConcessionsRefund / 100, 'EUR')
                            })}
                        </p>
                    )}
                </div>
                <div className="w-full lg:w-2/3 h-48 flex flex-row justify-center">
                    {/* Until the ticket system has issued the tickets the download answers 409, so wait for
                        the query above rather than letting the click fail silently (CIN-15227). */}
                    <Button
                        disabled={isLoadingTickets}
                        leadingIcon={
                            isPdfLoading || isLoadingTickets ? (
                                <IconLoading16 className="animate-spin" />
                            ) : (
                                <IconDownload16 />
                            )
                        }
                        onClick={async () => {
                            setIsPdfLoading(true);
                            track('Tickets Downloaded', {
                                format: 'PDF',
                                type: 'all',
                                ticketCount: ticketsWithPasses.length
                            });
                            const result = await downloadPdfTickets(paidCartUuid);
                            if (result === null) {
                                showToast({
                                    toastType: toast.error,
                                    messageTitle: t('error.ticket-download-failed')
                                });
                            }
                            setIsPdfLoading(false);
                        }}>
                        {isLoadingTickets ? t('preparing-tickets') : t('download-tickets')}
                    </Button>
                </div>
            </div>
            <div className="flex flex-col space-y-16">
                <div className="text-center">
                    <h2 className="text-headline-24">{t('your-tickets')}</h2>
                    <p className="text-body-14 opacity-70">
                        {t('order-number', { orderNumber: paidCart?.orderId ?? '' })}
                    </p>
                </div>
                {ticketsWithPasses.length ? (
                    <>
                        {/* GROUP TICKET */}
                        {ticketsWithPasses.length > 1 && (
                            <GroupTicketRow
                                cartUuid={paidCartUuid}
                                cartTicketCount={ticketsWithPasses.length}
                            />
                        )}

                        <div className="flex flex-row opacity-50 items-center space-x-16">
                            <span className="text-body-14">{t('single-tickets')}</span>
                            <hr className="grow" />
                        </div>
                        {ticketsWithPasses.map((ticketWithPass) => (
                            <TicketRow
                                key={ticketWithPass.ticket.ticketSystemId + '-' + ticketWithPass.ticket.id}
                                ticketWithPass={ticketWithPass}
                                title={t('selected-seat.title', {
                                    row:
                                        ticketWithPass.ticket._embedded?.seat?._embedded?.seatPosition?._embedded
                                            ?.seatRow?.name ?? '',
                                    seat: ticketWithPass.ticket._embedded?.seat?._embedded?.seatPosition?.name ?? ''
                                })}
                                cartUuid={paidCartUuid}
                                cartTicketCount={ticketsWithPasses.length}
                            />
                        ))}
                    </>
                ) : isLoadingTickets ? (
                    <div className="flex items-center justify-center">
                        <ProgressBarAnimation />
                    </div>
                ) : (
                    <div>{t('no-tickets-error')}</div>
                )}
            </div>
            {snacks.length > 0 && refusedConcessionsRefund === null && (
                <div className="flex flex-col space-y-16">
                    <h2 className="text-headline-24 text-center">{t('your-snacks')}</h2>
                    <p className="text-body-14 text-center">
                        {t(`pickup-${concessionPickupLocation.replace('_', '-')}`)}
                    </p>
                    <ul className="flex flex-col space-y-8">
                        {snacks.map((snack) => (
                            <li
                                key={snack.id}
                                className="text-body-14 text-center">
                                {snack.quantity} × {snack.name}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default PaymentSucceededView;
